import { supabase } from '@/lib/supabase/supabaseClient';
import { useSaveSurveyAsTemplateParams } from '.';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { prepareQuestionsWithNewIds } from '@/lib/utils/replaceOldIdsWithNewIds';
import { DEFAULT_CRM_SYSTEM } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen';
import { merge } from 'lodash';

type SurveyData = Tables<'survey'> & {
  question: Tables<'question'>[];
  crm: Tables<'crm'> | null;
  project: Tables<'project'> | null;
};

type SurveyBankData = Tables<'survey_bank'> & {
  question: Tables<'survey_bank_question_bank'>[];
  crm: Tables<'crm'> | null;
};

// Type guard to check if data is from survey_bank
const isSurveyBankData = (
  _data: SurveyData | SurveyBankData,
  source: 'surveyBank' | 'survey'
): _data is SurveyBankData => {
  return source === 'surveyBank';
};

const getSurveyData = async (surveyId: Tables<'survey'>['id']) => {
  return await supabase
    .from('survey')
    .select(
      `
            *,
            question(*),
            crm(*),
            project(*)
        `
    )
    .eq('id', surveyId)
    .maybeSingle()
    .throwOnError()
    .then((res) => res.data);
};

const getSurveyBankData = async (surveyId: Tables<'survey_bank'>['id']) => {
  return await supabase
    .from('survey_bank')
    .select(
      `
            *,
            question:survey_bank_question_bank(*),
            crm(*)
        `
    )
    .eq('id', surveyId)
    .maybeSingle()
    .throwOnError()
    .then((res) => res.data);
};

export const saveSurveyAsTemplate = async ({
  surveyId,
  source = 'survey',
  changes,
  suffixNameIfDidntChange,
}: useSaveSurveyAsTemplateParams) => {
  const surveyData =
    source === 'surveyBank'
      ? await getSurveyBankData(surveyId)
      : await getSurveyData(surveyId);

  if (!surveyData) {
    throw new Error('Survey not found');
  }

  const orgId = isSurveyBankData(surveyData, source)
    ? surveyData.organisation_id
    : surveyData.project!.organisation_id!;
  const surveyDataWithoutJointTables = { ...surveyData } as Partial<SurveyData>;
  console.log(surveyData);
  delete surveyDataWithoutJointTables?.question;
  delete surveyDataWithoutJointTables?.crm;
  delete surveyDataWithoutJointTables?.project;
  delete surveyDataWithoutJointTables?.id;

  delete changes?.id;
  const dataForNewSurveyTemplate = merge(
    {
      crm_system: DEFAULT_CRM_SYSTEM,
    },
    surveyDataWithoutJointTables,
    changes,
    {
      archived: false,
      organisation_id: orgId,
    },
    source !== 'surveyBank'
      ? {
          survey_ref_id: surveyId,
        }
      : {},
    suffixNameIfDidntChange
      ? {
          name:
            surveyData.name.trim() === (changes?.name?.trim() ?? '')
              ? `${surveyData.name} ${suffixNameIfDidntChange}`
              : changes?.name ?? surveyData.name,
        }
      : {}
  );
  const surveyBankData = await supabase
    .from('survey_bank')
    .insert(dataForNewSurveyTemplate)
    .select('*')
    .maybeSingle()
    .throwOnError()
    .then((res) => res.data);

  if (!surveyBankData) {
    throw new Error('Failed to create survey bank entry');
  }

  const questionsWithNewIds = prepareQuestionsWithNewIds({
    questions: surveyData.question,
  });
  const questionsWithRequiredColumns =
    questionsWithNewIds?.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ created_at, archived, survey_id, user_id, ...question }) => {
        return {
          ...question,
          survey_bank_id: surveyBankData.id,
          organisation_id: orgId,
        };
      }
    ) || [];
  try {
    await supabase
      .from('survey_bank_question_bank')
      .insert(questionsWithRequiredColumns)
      .select()
      .throwOnError()
      .then((res) => res.data);
    const questionsForQuestionBank = questionsWithRequiredColumns
      .filter((q) => !q.question_bank_ref_id)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ question_bank_ref_id, survey_bank_id, ...q }) => q);
    console.log(questionsForQuestionBank);
    for (const question of questionsForQuestionBank) {
      console.log(
        await supabase.from('question_bank').insert(question).throwOnError()
      );
    }

    return surveyBankData;
  } catch (error) {
    // Rollback: delete the survey_bank entry if questions fail, all questions will be deleted due to foreign key constraint
    await supabase
      .from('survey_bank')
      .delete()
      .eq('id', surveyBankData.id)
      .throwOnError();
    throw error;
  }
};
